import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  Inject,
  inject,
  Input,
  NgZone,
  OnDestroy,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ConversationApiService,
  ConversationChannel,
  GlobalParticipantType,
  InboxApiService,
  ListMessagesRequest,
  ListMessagesRequestListMessagesFilters,
  MessageInterface,
  MessageType,
  MetadataIdentifier,
  PagedRequestOptions,
  ParticipantType,
  SendMessageResponse,
  SetLastSeenRequestStatus,
  SubjectParticipant,
} from '@vendasta/conversation';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { FileInfo } from '@vendasta/galaxy/uploader';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { filterNullAndUndefined } from '@vendasta/rx-utils';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  Observable,
  of,
  ReplaySubject,
  switchMap,
  take,
  takeWhile,
  tap,
  zip,
  catchError,
} from 'rxjs';
import { getFollowViewID, toFirestoreId } from '../../../../core/src/lib/conversation-utils';
import { ConversationService } from '../../../../core/src/lib/state/conversation.service';
import { InboxNotificationService } from '../../../../core/src/lib/inbox-notification.service';
import { InboxTermsOfService } from '../../../../core/src/lib/inbox-terms-of-service.service';
import { InboxService } from '../../../../core/src/lib/inbox.service';
import {
  ConversationDetail,
  ConversationUnseen,
  convertMediaJSONIntoMedia,
  Media,
  MessageInfo,
  TermsOfServiceResult,
} from '../../../../core/src/lib/interface/conversation.interface';
import { HostAppInterface } from '../../../../core/src/lib/interface/host-app.interface';
import { isContactInformationMissing } from '../../../../core/src/lib/participant-utils';
import { ParticipantService } from '../../../../core/src/lib/participant.service';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  CONVERSATION_HOST_APP_INTERFACE_TOKEN,
  CONVERSATION_ROUTES_TOKEN,
  GROUP_ID_TOKEN,
  PARTNER_ID_TOKEN,
  USER_ID_TOKEN,
} from '../../../../core/src/lib/tokens';
import { ViewService } from '../../../../core/src/lib/view.service';
import { InboxMessageTextComponent } from './components/inbox-message-text/inbox-message-text.component';
import {
  InboxTermsOfServiceDialogComponent,
  TermsOfServiceData,
} from './components/inbox-terms-of-service/inbox-terms-of-service.component';
import { CardDataType } from '@vendasta/galaxy/contact-info-card';
import { MediaDialogComponent } from './components/inbox-messages/media-dialog.component';
import { AiResponder } from '../../../../core/src/lib/interface/assistant.interface';
import { ViewModeService } from '../../../../core/src/lib/view-mode.service';

type MessageWithMediaObject = Omit<MessageInterface, 'media'> & {
  media: [Media];
};

@Component({
  selector: 'inbox-chat',
  templateUrl: './inbox-chat.component.html',
  styleUrls: ['./inbox-chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InboxChatComponent implements OnDestroy {
  mediaMessages$$ = new BehaviorSubject<MessageWithMediaObject[]>([]);

  private isAlive = true;

  isDrawerOpened = false;

  private readonly conversationService = inject(ConversationService);
  private readonly viewModeService = inject(ViewModeService);
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  private readonly groupId$ = inject(GROUP_ID_TOKEN);
  readonly accountGroupId = toSignal(this.accountGroupId$);
  readonly partnerId = toSignal(this.partnerId$);
  readonly groupId = toSignal(this.groupId$);
  readonly userId = toSignal(inject(USER_ID_TOKEN));
  readonly routes$ = inject(CONVERSATION_ROUTES_TOKEN);

  @Input() set id(value: string) {
    this.conversationService.setCurrentFirestoreConversationId(value);
  }

  @Input() viewModel?: boolean;

  @ViewChild('inboxTextComponent') inboxTextComponent?: InboxMessageTextComponent;

  readonly isBusinessApp = this.inboxService.isBusinessApp;

  destroyRef = inject(DestroyRef);

  private readonly notificationWarningChat$$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  readonly notificationWarningChat$ = this.notificationWarningChat$$.asObservable();
  readonly isOpen = signal(false);
  protected readonly $loadingError = signal<Error | null>(null);

  readonly currentConversationDetail$ = this.conversationService.currentConversationDetail$.pipe(
    tap((conversationDetails) => {
      this.isOpen.set(conversationDetails?.conversation?.isOpen ?? false);
      this.$aiResponderEnabledForConversation.set(
        conversationDetails?.conversation?.aiConfiguration?.aiResponseEnabled ?? false,
      );
      this.$loadingError.set(null);
    }),
    catchError((err) => {
      console.error('error loading conversation', err);
      this.$loadingError.set(err);
      return of(null);
    }),
  );

  // flag to enable/disable AI response for the conversation
  protected readonly $aiResponderEnabledForConversation: WritableSignal<boolean> = signal(false);

  // flag that indicates if AI response is enabled for the conversation taking into account: feature flag, global configuration and conversation use case
  protected readonly $conversationAIResponder = signal<AiResponder | null>(null);

  private readonly $aiResponderForConversation = toSignal(this.conversationService.aiResponderForConversation$, {
    initialValue: null,
  });

  readonly currentConversationDetail = toSignal(this.currentConversationDetail$, {
    initialValue: null,
  });

  readonly $isConversationOverlayOpened = toSignal(this.viewModeService.isConversationOpen$, { initialValue: false });

  messages$ = this.conversationService.messages$;
  events$ = this.conversationService.events$;
  loadingMessages$ = this.conversationService.loadingMessages$;

  private readonly fileChange$$ = new ReplaySubject<FileInfo[]>(1);
  readonly fileChange$ = this.fileChange$$.asObservable();

  private partnerBrandName?: string;

  readonly unseenDefault: ConversationUnseen = {
    read: true,
    conversationId: '',
  };
  private readonly unseen$$: BehaviorSubject<ConversationUnseen> = new BehaviorSubject<ConversationUnseen>(
    this.unseenDefault,
  );
  readonly unseen$ = this.unseen$$.asObservable();

  private readonly isFollowing$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly isFollowing$ = this.isFollowing$$.asObservable();

  readonly isImpersonating$: Observable<boolean> = this.conversationService.isUserImpersonated$;

  readonly isOpenAIChannel$: Observable<boolean> = this.currentConversationDetail$.pipe(
    map(
      (conversation) =>
        conversation?.conversation?.channel === ConversationChannel.CONVERSATION_CHANNEL_OPENAI ||
        conversation?.conversation?.channel === ConversationChannel.CONVERSATION_CHANNEL_AI_ASSISTANT,
    ),
  );

  readonly isGoogleFacebookOrInstagramChannel$ = this.currentConversationDetail$.pipe(
    map((conversation): boolean =>
      [
        ConversationChannel.CONVERSATION_CHANNEL_GOOGLE_BUSINESS_COMMUNICATIONS,
        ConversationChannel.CONVERSATION_CHANNEL_FACEBOOK,
        ConversationChannel.CONVERSATION_CHANNEL_INSTAGRAM,
      ].includes(conversation?.conversation?.channel ?? ConversationChannel.CONVERSATION_CHANNEL_UNDEFINED),
    ),
  );

  readonly termsOfServiceResult$: Observable<TermsOfServiceResult> = combineLatest([
    this.inboxTermsOfService.getTermsOfServiceText$,
    this.inboxTermsOfService.termsOfServiceAccepted$,
    this.conversationService.isUserImpersonated$,
  ]).pipe(
    map(([termsOfServiceTextMessage, termsOfServiceAccepted, isImpersonating]) => {
      const showTOS = !isImpersonating && termsOfServiceTextMessage && !termsOfServiceAccepted;
      return {
        termsOfServiceAccepted: termsOfServiceAccepted,
        termsOfServiceTextMessage: showTOS ? termsOfServiceTextMessage : null,
      };
    }),
  );

  banners$ = this.currentConversationDetail$.pipe(
    map((conversation) => {
      if (this.isBusinessApp) return [];
      const participants =
        conversation?.participants
          ?.filter(
            (participant) =>
              participant.isSubjectParticipant &&
              participant.internalParticipantId &&
              (participant.participantType === ParticipantType.PARTICIPANT_TYPE_PARTNER ||
                participant.participantType === ParticipantType.PARTICIPANT_TYPE_VENDOR),
          )
          ?.map(
            (participant) =>
              new SubjectParticipant({
                // We only want to get and display banners for Partners and Vendors. Since currently we only allow
                // Partners to set configurations we must look for the Partner's configuration for that vendor.
                participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
                internalParticipantId: participant.internalParticipantId,
              }),
          ) || [];

      return Array.from(new Map(participants.map((p) => [p.internalParticipantId, p])).values());
    }),
    switchMap((participants) => {
      if (participants.length === 0) {
        return of(null);
      }
      return this.inboxApiService.getMultiConfiguration({
        subjectParticipants: participants,
      });
    }),
    map((configs) =>
      configs?.configurations
        ?.filter((config) => !!config?.availabilityMessage && config?.showAvailabilityMessage)
        ?.map((config) => config.availabilityMessage),
    ),
  );

  readonly inboxPrefilledMessage$ = this.route.queryParamMap.pipe(map((params) => params.get('inboxPrefilledMessage')));

  readonly isRecipientDeleted$ = this.currentConversationDetail$.pipe(
    filterNullAndUndefined(),
    map((conversation) => this.hostAppInterface.isRecipientInternalInfoDeleted(conversation)),
  );

  readonly availableChannels$ = this.conversationService.availableChannels$;

  readonly missingContactInformation$ = combineLatest([this.currentConversationDetail$, this.availableChannels$]).pipe(
    map(([conversation, availableChannels]) => {
      if (!conversation) {
        return false;
      }
      return isContactInformationMissing(conversation, availableChannels);
    }),
  );

  private participants$ = this.currentConversationDetail$.pipe(
    map((conversations) => conversations?.participants || []),
  );

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly snackbarService: SnackbarService,
    private readonly analyticsService: ProductAnalyticsService,
    private readonly inboxService: InboxService,
    private readonly inboxTermsOfService: InboxTermsOfService,
    private readonly viewService: ViewService,
    private readonly dialog: MatDialog,
    private readonly inboxNotificationService: InboxNotificationService,
    private inboxApiService: InboxApiService,
    private readonly participantService: ParticipantService,
    private readonly ngZone: NgZone,
    @Inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN) private readonly hostAppInterface: HostAppInterface,
    private conversationApiService: ConversationApiService,
  ) {
    effect(() => {
      this.$conversationAIResponder.set(this.$aiResponderForConversation());
    });

    effect(() => {
      const aiResponderEnabledForConversation = this.$aiResponderEnabledForConversation();
      this.$conversationAIResponder.update((x) => ({
        ...x,
        isConversationEnabled: aiResponderEnabledForConversation,
        isEnabled: x?.isEnabled ?? false,
      }));
    });

    // This is normally handled by the component input binding, which ties the named router param to directly set the
    // 'id' input. However, in Partner Center we are using an auxiliary outlet/named router outlet, and it doesn't
    // appear that component input bindings work for params defined in those routes. This subscription is a fallback to
    // ensure the value still gets set. Oct 11, 2023
    this.route.params.pipe(takeUntilDestroyed()).subscribe((params) => {
      if (params.id) {
        this.conversationService.setCurrentFirestoreConversationId(params.id);
      }
    });

    const orgId$ = this.isBusinessApp
      ? this.hostAppInterface.getAppOptions().is_multilocation
        ? this.groupId$
        : this.accountGroupId$
      : this.partnerId$;

    combineLatest([
      zip([orgId$.pipe(filter((orgId) => !!orgId)), this.routes$]),
      this.currentConversationDetail$.pipe(
        distinctUntilChanged((a, b) => a?.conversation?.conversationId === b?.conversation?.conversationId),
        filterNullAndUndefined(),
      ),
      this.conversationService.currentParticipant$,
    ])
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(([[currentOrg, routes], conversationDetail, currentParticipant]) => {
        if (
          conversationDetail?.conversation.subjectParticipants?.some(
            (p) =>
              p.internalParticipantId === currentOrg ||
              p.internalParticipantId === currentParticipant?.internalParticipantId,
          )
        ) {
          this.loadConversation(conversationDetail);
        } else {
          this.isAlive = false;
          this.conversationService.removeLastConversationId();
          this.router.navigate(routes.useModal ? ['', { outlets: { inbox: 'inbox' } }] : [routes.root + '/inbox']);
        }
      });
  }

  /**
   * load conversation using the new µs
   */
  private async loadConversation(conversationDetail: ConversationDetail): Promise<void> {
    if (conversationDetail && conversationDetail?.conversation?.conversationId) {
      this.analyticsService.trackEvent('inbox', 'conversation', 'view', 0, {
        id: toFirestoreId(conversationDetail.conversation.conversationId),
        accountGroupId: this.accountGroupId(),
        partnerId: this.partnerId(),
        location: this.inboxService.platformLocation,
        channel: conversationDetail?.conversation?.channel,
      });
      this.sendDraftMessage();
    }

    await this.conversationService.setConversationLastSeen(
      conversationDetail.conversation.conversationId as string,
      SetLastSeenRequestStatus.SET_LAST_SEEN_REQUEST_STATUS_READ,
    );
    this.unseen$$.next({
      conversationId: conversationDetail.conversation.conversationId,
      read: true,
    });

    this.conversationService.currentParticipant$
      .pipe(
        take(1),
        map(
          (participant) =>
            conversationDetail?.conversation?.conversationViewIds?.indexOf(
              getFollowViewID(participant.participantId),
            ) >= 0,
        ),
      )
      .subscribe((notInFollowIDs) => {
        this.isFollowing$$.next(notInFollowIDs);
      });

    const prefilledMessage = await firstValueFrom(this.inboxPrefilledMessage$);
    if (!prefilledMessage) {
      this.inboxTextComponent?.clearText();
    }
    this.inboxTextComponent?.removeFile();
    this.listMediaMessages(conversationDetail.conversation.conversationId);
  }

  private sendDraftMessage(): void {
    if (
      this.conversationService.draftMessages &&
      ((this.conversationService.draftMessages?.text && this.conversationService.draftMessages?.text !== '') ||
        this.conversationService.draftMessages?.attachments?.length > 0)
    ) {
      this.sendMessageConversation(this.conversationService.draftMessages);
    }
  }

  async sendMessage(messageInfo: MessageInfo): Promise<void> {
    const tosResult = await firstValueFrom(this.termsOfServiceResult$);
    let accepted = tosResult.termsOfServiceAccepted;
    if (!accepted && tosResult.termsOfServiceTextMessage) {
      accepted = await this.showTermsOfService({
        terms: tosResult.termsOfServiceTextMessage,
      });
      this.inboxTermsOfService.termsOfServiceAccepted$$.next(accepted);
    } else {
      const result = await this.sendMessageConversation(messageInfo);
      const following = await firstValueFrom(this.isFollowing$);
      if (result && !following) {
        this.simulateFollowingForBackgroundProcess();
      }
    }
  }

  fileChange(files: FileInfo[]): void {
    this.fileChange$$.next(files);
  }

  private simulateFollowingForBackgroundProcess(): void {
    if (this.isBusinessApp) {
      return;
    }
    this.isFollowing$$.next(true);
    this.snackbarService.openSuccessSnack('INBOX.VIEWS.SUCCESS_FOLLOW');
  }

  private async sendMessageConversation(messageInfo: MessageInfo): Promise<SendMessageResponse | undefined> {
    if ((messageInfo.text && messageInfo.text !== '') || messageInfo.template || messageInfo.attachments?.length > 0) {
      const result = await this.sendMessageViaConversation(messageInfo);
      this.conversationService.draftMessages = null;
      this.analyticsService.trackEvent('inbox', 'conversation', 'send-message', 0, {
        id: toFirestoreId(this.currentConversationDetail()?.conversation.conversationId ?? ''),
        accountGroupId: this.accountGroupId(),
        partnerId: this.partnerId(),
        location: this.inboxService.platformLocation,
        channel: messageInfo.channel,
      });
      return result;
    }
  }

  /**
   * send message using the new Conversation µs
   * @param {MessageInfo} messageInfo - the message text and media
   */
  private async sendMessageViaConversation(messageInfo: MessageInfo): Promise<SendMessageResponse> {
    const channel = messageInfo.channel;
    let metadata;
    let clearTextbox = true;

    if (messageInfo.template && messageInfo.channel === ConversationChannel.CONVERSATION_CHANNEL_WHATSAPP) {
      clearTextbox = false;
      metadata = [
        {
          identifier: MetadataIdentifier.METADATA_IDENTIFIER_RECIPIENT,
          data: {
            phoneNumber:
              this.currentConversationDetail()?.participants?.find((participant) => {
                return participant.participantType === ParticipantType.PARTICIPANT_TYPE_CUSTOMER;
              })?.phoneNumber ?? '',
          },
        },
        {
          identifier: MetadataIdentifier.METADATA_IDENTIFIER_WHATSAPP_TEMPLATE,
          data: {
            templateId: messageInfo.template?.id ?? '',
            ...messageInfo.template?.bodyParameters.reduce((acc: Record<number, string>, param) => {
              acc[param.field] = param.text;
              return acc;
            }, {}),
          },
        },
      ];
    }

    const result = await this.conversationService.sendAndStageMessage(
      this.currentConversationDetail()?.conversation.conversationId ?? '',
      messageInfo.template ? MessageType.MESSAGE_TYPE_TEMPLATE : MessageType.MESSAGE_TYPE_MESSAGE,
      messageInfo.text ?? '',
      channel,
      this.inboxService.platformLocation,
      messageInfo.attachments,
      undefined,
      undefined,
      metadata,
    );

    if (clearTextbox) {
      this.ngZone.run(() => {
        this.inboxTextComponent?.clearText();
      });
    }
    return result;
  }

  private async showTermsOfService(termsOfService: { terms: string }): Promise<boolean> {
    const data = {
      termsOfService: termsOfService.terms,
      accepted: false,
      partner: {
        name: this.partnerBrandName,
      },
    };

    const dialogRef: MatDialogRef<InboxTermsOfServiceDialogComponent, TermsOfServiceData> = this.dialog.open(
      InboxTermsOfServiceDialogComponent,
      {
        width: '600px',
        maxHeight: '90vh',
        autoFocus: false,
        data,
      },
    );

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (!result?.accepted) return false;

    const success = await firstValueFrom(this.inboxTermsOfService.acceptTermsOfService$());
    return success;
  }

  /**
   * TODO - Remove it when creating the new message status system
   * marking read is setting a date in the `lastSeenAt` attribute and unread is setting null
   * @param {{ read: boolean }} event - the event
   * @return {boolean}
   */
  async updateReadStatus(event: { read: boolean }): Promise<void> {
    const status = event?.read
      ? SetLastSeenRequestStatus.SET_LAST_SEEN_REQUEST_STATUS_READ
      : SetLastSeenRequestStatus.SET_LAST_SEEN_REQUEST_STATUS_UNREAD;

    await this.conversationService.setConversationLastSeen(
      this.currentConversationDetail()?.conversation.conversationId as string,
      status,
    );
    this.unseen$$.next({
      conversationId: this.currentConversationDetail()?.conversation.conversationId,
      read: event?.read,
    });
  }

  async updateFollowStatus(newFollowStatus: boolean): Promise<void> {
    if (this.isBusinessApp) {
      return;
    }
    const conversationDetails = await firstValueFrom(this.currentConversationDetail$);
    if (!conversationDetails) {
      return;
    }
    const conversationId = conversationDetails.conversation.conversationId;
    try {
      if (newFollowStatus) {
        await firstValueFrom(this.viewService.addConversationToConversationView(conversationId));
        this.snackbarService.openSuccessSnack('INBOX.VIEWS.SUCCESS_FOLLOW');
      } else {
        await firstValueFrom(this.viewService.removeConversationFromConversationView(conversationId));
      }
      this.isFollowing$$.next(newFollowStatus);
    } catch (error) {
      this.snackbarService.openErrorSnack('INBOX.VIEWS.FAILED_FOLLOW');
    }
  }

  async updateOpenStatus(newOpenStatus: boolean): Promise<void> {
    const conversationId = this.currentConversationDetail()?.conversation.conversationId;
    if (!conversationId) {
      return;
    }

    const conversation = await this.conversationService.setConversationOpenStatus(conversationId, newOpenStatus);
    this.isOpen.set(conversation.isOpen);
  }

  async handleTitleClickInBusinessApp(status: boolean): Promise<void> {
    this.isDrawerOpened = status;
  }

  async changeAIResponseStatus(isAIResponseEnabled: boolean): Promise<void> {
    this.$aiResponderEnabledForConversation.set(isAIResponseEnabled);
  }

  openConversationOverlay(openOverLayClicked: boolean): void {
    if (openOverLayClicked) {
      this.viewModeService.openConversationOverlay(
        this.currentConversationDetail()?.conversation?.conversationId ?? '',
        true,
      );
    }
  }

  closeConversationOverlay(closeOverlayClicked: boolean): void {
    if (closeOverlayClicked) {
      this.viewModeService.close();
    }
  }

  openSettings(): void {
    this.inboxNotificationService
      .turnOnOnlyMyEventsNotification$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.notificationWarningChat$$.next(false);
          this.snackbarService.openSuccessSnack('INBOX.WARNING.NOTIFICATION_UPDATE_SUCCESS');
        },
        error: () => {
          this.snackbarService.openErrorSnack('INBOX.WARNING.NOTIFICATION_UPDATE_ERROR');
        },
      });
  }

  onClose(): void {
    this.notificationWarningChat$$.next(false);
    this.inboxNotificationService.setCookie();
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }

  closeDrawer(): void {
    this.isDrawerOpened = false;
  }

  async hasBusinessAppSideBar(): Promise<boolean> {
    return this.isDrawerOpened;
  }

  readonly cardData$ = this.currentConversationDetail$.pipe(
    map((conversationDetail) =>
      conversationDetail?.participants
        .filter((participant) => !!participant)
        .filter((participant) => participant?.isSubjectParticipant)
        .filter(
          (participant) =>
            !(
              participant.participantType === ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP &&
              participant.accountGroupId === this.accountGroupId()
            ),
        )
        .map((participant): CardDataType => {
          const { name, participantType, email, phoneNumber, profileImageUrl } = participant;
          const { firstLineAddress, secondLineAddress, city, state, country, zipCode } = participant.address || {};
          const cardType = participantType === ParticipantType.PARTICIPANT_TYPE_CUSTOMER ? 'user' : 'business';
          return {
            type: cardType,
            email: email,
            phone: phoneNumber,
            avatar: profileImageUrl,
            businessName: cardType === 'business' ? name : '',
            userName: name || email || phoneNumber,
            businessAddress: {
              address1: firstLineAddress,
              address2: secondLineAddress,
              city: city,
              state: state,
              country: country,
              zip: zipCode,
            },
            profileButtonAction:
              cardType === 'user' ? () => this.hostAppInterface.viewContact(conversationDetail) : undefined,
          };
        }),
    ),
  );

  async listMediaMessages(conversationId: string): Promise<void> {
    const filters = new ListMessagesRequestListMessagesFilters({ conversationId: conversationId });
    const pagingOptions = new PagedRequestOptions({ pageSize: 40 });
    const request = new ListMessagesRequest({ filters: filters, pagingOptions: pagingOptions });
    const response = await firstValueFrom(this.conversationApiService.listMessages(request));
    const mediaMessages: MessageWithMediaObject[] = (response.messages || [])
      .map((message): MessageWithMediaObject | null => {
        if (message.media && message.media.length > 0) {
          const mediaObject = convertMediaJSONIntoMedia(message.media[0]);
          if (!mediaObject) {
            return null;
          }
          if (mediaObject === 'not-json') {
            return {
              ...message,
              media: [
                {
                  mediaContentType: 'broken',
                  fileSize: 0,
                  mediaFileName: '',
                  mediaLocationPath: '',
                  mediaUrl: '',
                },
              ],
            };
          }
          return { ...message, media: [mediaObject] };
        }
        return null;
      })
      .filter((message): message is MessageWithMediaObject => !!message)
      .filter(
        (message) =>
          message.media &&
          (message.media[0].mediaContentType.startsWith('image/') ||
            message.media[0].mediaContentType.startsWith('broken') ||
            message.media[0].mediaContentType.startsWith('video/')),
      );
    this.mediaMessages$$.next(mediaMessages);
  }

  openInDialog(url: string, contentType: string) {
    this.dialog.open(MediaDialogComponent, {
      data: { mediaUrl: url, mediaContentType: contentType },
      height: 'auto',
      maxWidth: '860px',
    });
  }
}
